/**
 * Google Sheets API service for direct sheet access.
 *
 * Expected sheet structure (row 1 = header, data starts row 2):
 *   A: name  B: email  C: mobileNo  D: internshipTopic  E: collegeName
 *   F: password (hashed)  G: courseName  H: courseYear  I: createdAt  J: isAdmin
 *
 * Read operations use REACT_APP_GOOGLE_API_KEY (no OAuth needed, sheet must be shared as "anyone can view").
 * Write operations (appendUser) use a Google OAuth access token.
 */

const BASE = "https://sheets.googleapis.com/v4/spreadsheets";
const SPREADSHEET_ID = process.env.REACT_APP_SPREADSHEET_ID;
const SHEET_NAME = process.env.REACT_APP_SHEET_NAME || "Users";
const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;

export const COLS = {
  NAME: 0,
  EMAIL: 1,
  MOBILE: 2,
  TOPIC: 3,
  COLLEGE: 4,
  PASSWORD: 5,
  COURSE: 6,
  YEAR: 7,
  CREATED: 8,
  IS_ADMIN: 9,
};

/** Reads all data rows using an API key (no OAuth required). */
const fetchRows = async () => {
  const range = encodeURIComponent(SHEET_NAME + "!A2:J");
  const res = await fetch(
    `${BASE}/${SPREADSHEET_ID}/values/${range}?key=${API_KEY}`
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error?.message || "Failed to read sheet");
  }
  const data = await res.json();
  return data.values || [];
};

const rowToUser = (row) => ({
  name: row[COLS.NAME] || "",
  email: row[COLS.EMAIL] || "",
  mobileNo: row[COLS.MOBILE] || "",
  internshipTopic: parseInt(row[COLS.TOPIC]) || "",
  collegeName: row[COLS.COLLEGE] || "",
  courseName: row[COLS.COURSE] || "",
  courseYear: row[COLS.YEAR] || "",
  createdAt: row[COLS.CREATED] || "",
  isAdmin: row[COLS.IS_ADMIN] === "true",
});

/** Returns all users. Uses API key — no OAuth needed. */
export const getAllUsers = async () => {
  const rows = await fetchRows();
  return rows.map(rowToUser);
};

/** Finds a user by email. Uses API key — no OAuth needed. */
export const getUserByEmail = async (email) => {
  const rows = await fetchRows();
  const row = rows.find(
    (r) => r[COLS.EMAIL]?.toLowerCase() === email?.toLowerCase()
  );
  return row ? rowToUser(row) : null;
};

/**
 * Verifies email + hashed password. Uses API key — no OAuth needed.
 * Returns the user object if credentials match, null otherwise.
 */
export const verifyCredentials = async (email, hashedPassword) => {
  const rows = await fetchRows();
  const row = rows.find(
    (r) =>
      r[COLS.EMAIL]?.toLowerCase() === email?.toLowerCase() &&
      r[COLS.PASSWORD] === hashedPassword
  );
  return row ? rowToUser(row) : null;
};

/**
 * Appends a new user row. Requires a Google OAuth access token with Sheets scope.
 * userData: { name, email, mobileNo, internshipTopic, collegeName, password, courseName, courseYear }
 */
export const appendUser = async (accessToken, userData) => {
  const values = [
    userData.name,
    userData.email,
    userData.mobileNo,
    String(userData.internshipTopic),
    userData.collegeName,
    userData.password,
    userData.courseName,
    userData.courseYear,
    new Date().toISOString(),
    "false",
  ];

  const range = encodeURIComponent(SHEET_NAME + "!A:J");
  const res = await fetch(
    `${BASE}/${SPREADSHEET_ID}/values/${range}:append?valueInputOption=USER_ENTERED`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ values: [values] }),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error?.message || "Failed to write to sheet");
  }
  return res.json();
};